import React, { useContext, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';

import DashboardContext from '#/context';
import useFilters from '#/hooks/useFilters';
import useSuccessModal from '#/hooks/useSuccessModal';
import Tabs from '#/containers/Tabs';
import Filters from '#/containers/Filters/index';
import Dashboard from '#/containers/Dashboard';
import WidgetBoard from '#/containers/WidgetBoard';
import SuccessFlag from '#/components/SuccessFlag';
import AlertIcon from '#/components/AlertIcon';
import Button from '#/components/Button';
import { saveWidgets, fetchActiveWidgets } from '#/api/widgetService';
import alertIcon from '#/assets/icons/alert.svg';
import FilterButton from '#/components/FilterButton';
import Modal from '#/components/Modal';
import useModal from '#/hooks/useModal';
import Dropdown from '#/components/Dropdown';
import styles from './styles.module.css';

import {useTabs} from '#/contexts/tabscontexts';
import { DASHBOARD_TYPE } from "./enums";
import { useTranslation } from "react-i18next";

function Workspace() {
  const {
    date,
    setLoading,
    hasCookie,
    localFilters,
    setStateDashboard,
    stateDashboard,
    setTempDashboard,
    tempDashboard,
    setActiveDashboard,
    activeDashboard,
    setPolling,
    polling,
  } = useContext(DashboardContext);

  const { modal, toggleModal } = useModal();
  const { isOpen, toggleFilter } = useFilters();
  const { isModalOpen, showModal, closeModal } = useSuccessModal();
 
  const {activeTabIndex, setActiveTabIndex } = useTabs();

  const { t } = useTranslation();

  useEffect(() => {}, [
    activeTabIndex,
    hasCookie,
    date,
    stateDashboard,
    activeDashboard,
    tempDashboard,
  ]);

  function handleEdit() {
    setTempDashboard(JSON.parse(JSON.stringify(activeDashboard)));
    setStateDashboard(stateDashboard => ({
      ...stateDashboard,
      edit: true,
      board: true,
    }));
    if (isOpen) toggleFilter();
  }

  async function handleSave(isShared = false) {
    const parseDashboard = tempDashboard.map(dashboard => {

      if((isShared && dashboard.isNewDashboard) || (isShared && dashboard.ordination === activeTabIndex+1)) {
        dashboard.type = DASHBOARD_TYPE.SHARED;
      } else if (!dashboard.type) {
        dashboard.type = DASHBOARD_TYPE.STANDARD;
      }

      const widgets = dashboard.widgets.map(({ dashboardWidgetId, i, w, h, x, y, defaultBackgroundColor, defaultFontColor, title, exhibition, ordination, widgetBindDtos, auxiliaryOptionsDto }) => {
        return { dashboardWidgetId, i, w, h, x, y, defaultBackgroundColor, defaultFontColor, title, exhibition, ordination, widgetBindDtos, auxiliaryOptionsDto };
      });
      return { ...dashboard, widgets };
    });
    try {
      setLoading(true);
      await saveWidgets(parseDashboard);
      
      const { data } = await fetchActiveWidgets();
      if (!data.length) {
        setActiveDashboard([
          ...activeDashboard,
          {
            id: '1',
            name: t('UNTITLED'),
            ordination: 1,
            widgets: [],
          },
        ]);
      } else {
        const newData = data.map(dash => {
          return {
            widgets: [],
            ...dash,
          };
        });
        setActiveDashboard(newData);
      }
      
      setTempDashboard([]);
      setStateDashboard({ ...stateDashboard, edit: false });
      showModal();
      setLoading(false);
    } catch ({ response }) {
      setLoading(false);
      if (response.status === 401) {
        return toggleModal(
          t('SESSION_HAS_BEEN_EXPIRED')
        );
      }
      toggleModal(
        t('CANNOT_SAVE_DASHBOARD')
      );
    }
  }

  function handleCancel() {
    setTempDashboard([]);
    setActiveDashboard([]);
    setStateDashboard({ ...stateDashboard, edit: false });
    setTimeout(() => setActiveDashboard(activeDashboard));
    setTimeout(
      () =>
        activeTabIndex >= activeDashboard.length &&
        setActiveTabIndex(activeDashboard.length - 1)
    );
  }

  function isEqual() {
    return JSON.stringify(tempDashboard) === JSON.stringify(activeDashboard);
  }

  function isDisabled() {
    return !!tempDashboard.find(({ widgets }) => !widgets.length);
  }

  function onSelectDropdown(item) {
    localStorage.setItem('polling', item);
    setPolling(item);
  }

  const intervalPolling = [
    { id: 1, name: `5 ${t('MINUTES')}`, value: 300000 },
    { id: 2, name: `10 ${t('MINUTES')}`, value: 600000 },
    { id: 3, name: `15 ${t('MINUTES')}`, value: 900000 },
    { id: 4, name: `30 ${t('MINUTES')}`, value: 1800000 },
    { id: 5, name: `${t('NEVER')}`, value: 0 },
  ];

  return (
    <main>
      <Modal
        title={t('SOMETHING_WENT_WRONG')}
        isOpen={modal.isOpen}
        onClose={() => {
          toggleModal();
          setStateDashboard({ ...stateDashboard, edit: false });
        }}
      >
        {modal.message}
      </Modal>
      <SuccessFlag isOpen={isModalOpen} onClose={closeModal} />
      <Filters hide={toggleFilter} isOpen={isOpen} />
      <div className={styles.header}>
        <Tabs/>
        <div className={styles.editButtons}>
          {stateDashboard.edit ? (
            <React.Fragment>
              <CSSTransition
                in={!isEqual()}
                mountOnEnter
                unmountOnExit
                timeout={200}
                classNames={{
                  enter: styles['move-enter'],
                  enterActive: styles['move-enter-active'],
                  exit: styles['move-exit'],
                  exitActive: styles['move-exit-active'],
                }}
              >
                <AlertIcon imagePath={alertIcon}>
                  {t('PENDING_CHANGES_ALERT')}
                </AlertIcon>
              </CSSTransition>
              <Button
                buttonType="secondary"
                action={handleCancel}
                data-testid="cancel-button"
              >
                {t('CANCEL')}
              </Button>
              <Button
                buttonType="primary"
                action={handleSave}
                isDisabled={isDisabled()}
                isDropDown={true}
                disabledText={t('EMPTY_DASHBOARD')}
                data-testid="save-button"
              >
                {t('SAVE')}
              </Button>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Dropdown
                itens={intervalPolling}
                onSelect={onSelectDropdown}
                active={polling}
              />
              <FilterButton
                isOpen={isOpen}
                toggle={toggleFilter}
                filters={localFilters}
              />
              <Button
                buttonType="primary"
                action={handleEdit}
                data-testid="edit-button"
              >
                {t('EDIT')}
              </Button>
            </React.Fragment>
          )}
        </div>
      </div>
      <Dashboard />
      {stateDashboard.edit && <WidgetBoard />}
    </main>
  );
}

export default Workspace;
